/**
 * GA4 funnel events — `begin_checkout` and `add_payment_info` (WP-69). The `purchase` event is
 * never sent from here: it is sent server-side, from `Analytics\AnalyticsService`, only once
 * `Donations\DonationCompleter` has recorded a gift against a verified gateway webhook
 * (CLAUDE.md rule 6 — "no client-originated request may mark a donation complete", which extends
 * to *reporting* one as given).
 *
 * `gtag.js` is loaded only when `window.AnudanEngine.config.analytics` is present — set by
 * `Frontend\CheckoutAssets` only when an administrator has turned Analytics on and saved a
 * Measurement ID. No config, no script tag, no event: fail closed, off by default, the same
 * shape `turnstile.js` already uses for a third-party script load.
 *
 * Every value passed in here is one the wizard already received from the server — `lastQuote`'s
 * `total_charged` (via `/checkout/quote`, A-1: "the client displays; it never calculates") and
 * the fund's own schema-provided name — never a figure this file computes itself.
 */

let scriptPromise = null;
let beginCheckoutSentForFund = null;

/**
 * @return {{measurementId: string}|null}
 */
function analyticsConfig() {
	const engine = window.AnudanEngine;

	if ( ! engine || ! engine.config || ! engine.config.analytics || ! engine.config.analytics.measurementId ) {
		return null;
	}

	return engine.config.analytics;
}

/**
 * Loads `gtag.js` once per page, lazily — never on page load, only once a donor has actually
 * reached a point this module wants to report.
 *
 * @param {string} measurementId
 * @return {Promise<boolean>} Resolves `true` once `window.gtag` is callable, `false` if the
 *                             script never loaded.
 */
function loadScript( measurementId ) {
	if ( scriptPromise ) {
		return scriptPromise;
	}

	window.dataLayer = window.dataLayer || [];
	window.gtag = window.gtag || function gtag() {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer.push( arguments );
	};
	window.gtag( 'js', new Date() );
	window.gtag( 'config', measurementId, { send_page_view: false } );

	scriptPromise = new Promise( ( resolve ) => {
		const script = document.createElement( 'script' );

		script.src = `https://www.googletagmanager.com/gtag/js?id=${ encodeURIComponent( measurementId ) }`;
		script.async = true;
		script.addEventListener( 'load', () => resolve( true ) );
		script.addEventListener( 'error', () => resolve( false ) );
		document.head.appendChild( script );
	} );

	return scriptPromise;
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} params
 * @return {void}
 */
function send( eventName, params ) {
	const config = analyticsConfig();

	if ( ! config ) {
		return;
	}

	loadScript( config.measurementId );

	window.gtag( 'event', eventName, params );
}

/**
 * @param {{totalMinorUnits: number, currency: string, fundId: number, fundName: string}} detail
 * @return {Record<string, unknown>}
 */
function eventParams( detail ) {
	return {
		currency: detail.currency || 'USD',
		value: Math.round( detail.totalMinorUnits ) / 100,
		items: [
			{
				item_id: String( detail.fundId ),
				item_name: detail.fundName || 'Donation',
				quantity: 1,
			},
		],
	};
}

/**
 * Fired once per fund, the first time the donor reaches the payment step. Not re-sent on a
 * back-and-forward navigation within the same wizard instance.
 *
 * @param {{totalMinorUnits: number, currency: string, fundId: number, fundName: string}} detail
 * @return {void}
 */
export function trackBeginCheckout( detail ) {
	if ( beginCheckoutSentForFund === detail.fundId ) {
		return;
	}

	beginCheckoutSentForFund = detail.fundId;

	send( 'begin_checkout', eventParams( detail ) );
}

/**
 * Fired once the donor has a payment method in hand and checkout is proceeding to confirmation —
 * the gateway's own element has loaded (standard lane) or a wallet sheet has resolved
 * successfully (express/wallet lane).
 *
 * @param {{totalMinorUnits: number, currency: string, fundId: number, fundName: string, paymentType: string}} detail
 * @return {void}
 */
export function trackAddPaymentInfo( detail ) {
	send( 'add_payment_info', { ...eventParams( detail ), payment_type: detail.paymentType } );
}

/**
 * Test-only reset of the per-fund `begin_checkout` guard.
 *
 * @return {void}
 */
export function resetAnalyticsStateForTests() {
	beginCheckoutSentForFund = null;
	scriptPromise = null;
}
